import { Button, chakra, Flex, Icon, IconButton, Text } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useResource, useResourcesInfinite } from 'datx-jsonapi-react';
import { BaseEmoji } from 'emoji-mart';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

import CloseIcon from '@/assets/icons/ic-close.svg';
import SendIcon from '@/assets/icons/ic-send-message.svg';

import { ChatProfile } from '@/components/ui/shared/chat/ChatPopup/components/types';
import { NewChatEmojiPicker } from '@/components/ui/shared/conversation/Chat/NewChatEmojiPicker';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { queryAllChatMessages } from '@/queries/chat-messages';
import { queryChatChannelOfUser } from '@/queries/event-chat-channels';
import {
  channelUnreadNotificationsQueryOptions,
  markNotificationRead,
  NOTIFICATIONS_QUERY_KEY,
} from '@/queries/upgrade/notifications';
import { ChatMessages } from '@/resources/ChatMessages';
import { EventChatChannel } from '@/resources/EventChatChannel';

const AutoResizeInput = chakra(TextareaAutosize);

interface IMessageInputProps {
  channel?: EventChatChannel;
  profile: ChatProfile;
  message: ChatMessages;
  setEditMessage: (message: ChatMessages | null) => void;
}

interface IMessageFormProps {
  updateContent: string;
}

export const EditMessageInput: FC<IMessageInputProps> = ({ channel, profile, message, setEditMessage }) => {
  const { register, handleSubmit, reset, watch, setFocus, setValue, getValues } = useForm<IMessageFormProps>();
  const { store } = useStores();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { mutate: mutateMessages } = useResourcesInfinite(queryAllChatMessages(channel?.id));
  const { mutate: mutateChannel } = useResource(queryChatChannelOfUser(profile.user.id, eventId));

  const queryClient = useQueryClient();
  const { data: notifications } = useQuery(channelUnreadNotificationsQueryOptions(channel?.id));

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateContentRef = useRef(null);
  const { ref, ...rest } = register('updateContent');

  async function submitMessage({ updateContent }: IMessageFormProps) {
    try {
      setIsLoading(true);

      if (channel) {
        await updateMessage(updateContent, channel.id);
        return;
      }

      const response = await store.request(
        `${EventChatChannel.type}?include=event_chat_channel_participants.user.main_profile`,
        'POST',
        {
          data: {
            type: EventChatChannel.type,
            attributes: {
              event_id: eventId,
              participant_ids: [profile.user.id],
            },
          },
        }
      );

      const eventChatChannel = response?.data as EventChatChannel;

      await updateMessage(updateContent, eventChatChannel.id);

      await mutateChannel();
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function updateMessage(content: string, channelId: string) {
    try {
      await store.request(`${ChatMessages.type}/${message.id}`, 'PATCH', {
        data: {
          type: ChatMessages.type,
          attributes: {
            channel_id: channelId,
            content,
          },
        },
      });

      await mutateMessages();
      setEditMessage(null);
      reset();
    } catch (err) {
      console.log(err);
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      handleSubmit((data) => {
        submitMessage(data);
      })();
    }
  };

  useEffect(() => {
    // this line will not trigger markNotificationAsRead because notifications are still loading, but we need focus anyway
    setFocus('updateContent');
    if (notifications && notifications.length > 0) {
      markNotificationAsRead();
    }
  }, [notifications, setFocus]);

  useEffect(() => {
    const length = message.content.length;

    updateContentRef.current.setSelectionRange(length, length);
  }, [message.content]);

  async function markNotificationAsRead() {
    if (!channel) return;
    if (channel.unreadMessages > 0 && notifications && notifications.length > 0) {
      const channelNotification = notifications[0];

      try {
        await markNotificationRead(channelNotification.id);

        queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      } catch (err) {
        console.log(err);
      }
    }
  }

  const onEmojiSelect = (emoji: BaseEmoji) => {
    const currentContent = getValues('updateContent');

    setValue('updateContent', currentContent + emoji.native);
  };

  return (
    <form onSubmit={handleSubmit(submitMessage)}>
      <Text w="full" borderBottom="1px solid black" paddingX="-12px" />
      <Flex justify="space-between" my={4} alignItems="center">
        <Text fontWeight="bold">Edit message</Text>
        <IconButton
          size="xs"
          onClick={() => setEditMessage(null)}
          aria-label="Cancel editing"
          ml="2"
          variant="unstyled"
          icon={<CloseIcon width="24px" />}
        />
      </Flex>
      <Flex alignItems="center" border="1px" overflow="hidden" borderColor="gray.200" py={3} px={2} borderRadius={4}>
        <AutoResizeInput
          resize="none"
          w="full"
          {...rest}
          ref={(e) => {
            ref(e);
            updateContentRef.current = e;
          }}
          defaultValue={message.content}
          onKeyDown={handleKeyDown}
          outline="none"
          borderRadius="4px"
          onFocus={markNotificationAsRead}
          px={2}
          mr={1}
          maxRows={5}
          _disabled={{ opacity: '0.6' }}
          // disabled={isLoading}
          placeholder="Write a message..."
        />

        <NewChatEmojiPicker onEmojiSelect={onEmojiSelect} />
        <Button
          variant="link"
          colorScheme="violet"
          aria-label="Send message"
          alignSelf="flex-end"
          maxW="min-content"
          minH={8}
          type="submit"
          disabled={!watch('updateContent')}
          isLoading={isLoading}
        >
          <Icon w="24px" h="24px" ml={1} as={SendIcon} />
        </Button>
      </Flex>
    </form>
  );
};
